/**
 * Pure decision logic for the CarlaChatPane hydrate effect.
 *
 * Extracted from the effect body so the behavior can be unit-tested
 * across all scenarios (Phase A 2026-04-26 root-cause was a wrong
 * branch order; this pulls the logic out where it can't drift again).
 *
 * Three flows the effect needs to discriminate:
 *
 *   (A) Hydrate from server-side seed.
 *       User clicked an existing conversation; parent bumped
 *       `chatInstanceKey` to force CarlaChatPane remount. Once the
 *       seed query finishes, push its messages into the hook state.
 *
 *   (B) Live stream just minted an id.
 *       The hook returned X-Conversation-ID header → liveConversationId
 *       flipped from null → newId. The streaming assistant message
 *       lives in the hook's local `messages` array; do NOT overwrite
 *       it with the (possibly stale) seed query result.
 *
 *   (C) Reset — `conversationId` is null and no live stream is active.
 *       User clicked "+ New" or deleted the current conversation.
 *       Wipe local state to a clean slate.
 *
 *   (noop) Don't touch state.
 *       During an active stream — pre-X-Conversation-ID, post-X-
 *       Conversation-ID-but-pre-prop-update — local state is in
 *       flight. Touching it (especially calling newConversation)
 *       wipes the optimistic user + streaming assistant placeholder.
 *
 * Phase A bug (2026-04-26): the original effect ran the !conversationId
 * reset branch BEFORE the isStreaming guard. Mid-stream, prop=null
 * and isStreaming=true; the reset wiped the optimistic state every
 * time. Fix: isStreaming guard fires FIRST.
 */

export type HydrateAction =
  | 'noop'         // skip; no state change
  | 'reset'        // call newConversation()
  | 'mark_b_flow' // set hydratedForRef = conversationId, then skip
  | 'wait_seed'   // skip; seed query still loading
  | 'hydrate_a'   // call hydrateFromHistory(seed, conversationId)

export interface HydrateGuardInputs {
  /** Current prop conversationId from CarlaPage. */
  conversationId: string | null
  /** Hook-local conversation id; mints when stream returns X-Conversation-ID. */
  liveConversationId: string | null
  /** Prop value captured at mount via initialIdRef. */
  initialId: string | null
  /** Conversation id we've already hydrated for in this mount. */
  hydratedFor: string | null
  /** True while a chat stream is in flight (sendMessage call hasn't finished). */
  isStreaming: boolean
  /** True while the parent's seed-history TanStack Query is loading. */
  isLoadingSeed: boolean
}

/**
 * Decide what the hydrate effect should do given the current state.
 *
 * Order of checks matters — this is the load-bearing fix from
 * Sprint Z Chunk 1. Don't reorder without understanding the flow.
 */
export function decideHydrateAction(inputs: HydrateGuardInputs): HydrateAction {
  // (1) Active stream — never touch state. This MUST be the first
  // check because during a stream the prop conversationId may still
  // be null while the hook's optimistic messages are mid-update.
  if (inputs.isStreaming) return 'noop'

  // (2) No conversation selected and no live stream → fresh slate.
  if (!inputs.conversationId) return 'reset'

  // (3) Already hydrated for this conversation — nothing to do.
  if (inputs.hydratedFor === inputs.conversationId) return 'noop'

  // (4) Flow (B): live stream just minted this id. The local state
  // already holds the streaming reply; don't overwrite it with the
  // seed query result.
  const wasInitiallyMounted = inputs.initialId === inputs.conversationId
  if (
    !wasInitiallyMounted &&
    inputs.liveConversationId === inputs.conversationId
  ) {
    return 'mark_b_flow'
  }

  // (5) Flow (A): wait for seed query before hydrating; otherwise we
  // hydrate with [] during the loading window and lose history.
  if (inputs.isLoadingSeed) return 'wait_seed'

  return 'hydrate_a'
}
